
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Homework, StudentHomeworkStatus, HomeworkTask, StuckLog, TutorLog, StudentConnection, UserProfile, Organization, Interaction } from '../types';
import { ProgressRing, ImageUpload, VoiceInput, Avatar } from './common';
import { ListenButton } from './ListenButton';
import { Type } from '@google/genai';
import { generateContentWithRetry, translateContent, generateSpeech } from '../lib/gemini';
import { LANGUAGES } from '../lib/constants';
import { isParentPolicyEnabled } from '../lib/features';
import { getTaskBrainScore, computeDayBrainMetrics } from '../lib/brainPowerUtils';
import { getAttachmentType, getAttachmentFileName } from '../lib/helpers';

// --- Helper: Subject Color Generator ---
const getSubjectColor = (subject: string) => {
    const colors = [
        'bg-red-400', 'bg-blue-400', 'bg-green-400', 'bg-yellow-400', 
        'bg-purple-400', 'bg-pink-400', 'bg-indigo-400', 'bg-teal-400'
    ];
    let hash = 0;
    for (let i = 0; i < subject.length; i++) {
        hash = subject.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

// --- Helper: Parse Markdown Links ---
const renderContentWithLinks = (text: string) => {
    if (!text) return null;
    // Split by markdown link pattern [text](url)
    const parts = text.split(/(\[.*?\]\(.*?\))/g);
    return parts.map((part, i) => {
        const match = part.match(/^\[(.*?)\]\((.*?)\)$/);
        if (match) {
            return (
                <a 
                    key={i} 
                    href={match[2]} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 underline hover:text-blue-800 font-medium"
                >
                    {match[1]}
                </a>
            );
        }
        return part;
    });
};

// --- Component: Tutor Bubble with Translation ---
const TutorBubble = ({ text, isStudent, studentName, userLanguage }: { text: string, isStudent: boolean, studentName?: string, userLanguage: string }) => {
    const [translatedText, setTranslatedText] = useState<string | null>(null);
    const [showingTranslated, setShowingTranslated] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setTranslatedText(null);
        setShowingTranslated(false);
    }, [userLanguage, text]);

    const canTranslate = Boolean(text && text.trim().length > 0);
    const targetLangLabel = LANGUAGES.find(l => l.code === userLanguage)?.label || userLanguage;

    const toggleTranslate = async () => {
        if (!translatedText) {
            setLoading(true);
            try {
                const res = await translateContent(text, userLanguage);
                setTranslatedText(res);
                setShowingTranslated(true);
            } catch (e) {
                console.error("Translation failed", e);
            } finally {
                setLoading(false);
            }
        } else {
            setShowingTranslated(!showingTranslated);
        }
    };

    const content = showingTranslated && translatedText ? translatedText : text;

    return (
        <div className={`flex flex-col ${isStudent ? 'items-end' : 'items-start'} mb-2 animate-slide-up`}>
            <div className={`flex ${isStudent ? 'justify-end' : 'justify-start'} w-full`}>
                 {!isStudent && (
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0 self-end mb-1">
                        <span className="material-icons-round text-indigo-600 text-sm">smart_toy</span>
                    </div>
                )}
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm shadow-sm leading-relaxed ${
                    isStudent 
                        ? 'bg-indigo-600 text-white rounded-br-none' 
                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                }`}>
                    <p className="whitespace-pre-wrap">
                        {isStudent ? content : renderContentWithLinks(content || '')}
                    </p>
                </div>
                {isStudent && (
                    <div className="ml-2 self-end mb-1">
                        <Avatar name={studentName || ''} size="sm" />
                    </div>
                )}
            </div>
            <div className={`flex items-center gap-2 mt-1 px-1 ${isStudent ? 'justify-end mr-12' : 'justify-start ml-12'}`}>
                {canTranslate && (
                    <button 
                        onClick={toggleTranslate}
                        disabled={loading}
                        className="text-[11px] font-bold opacity-80 hover:opacity-100 transition-opacity flex items-center gap-0.5 text-indigo-600"
                    >
                        <span className="material-icons-round text-[12px]">translate</span>
                        {loading ? 'Translating...' : (showingTranslated ? 'Show Original' : `Translate (${targetLangLabel})`)}
                    </button>
                )}
                <ListenButton
                    id={`tutor-${text.substring(0, 32)}`}
                    text={content}
                    language={showingTranslated ? userLanguage : 'en'}
                    variant={isStudent ? 'bubble-own' : 'bubble-other'}
                    label="Listen"
                    stopLabel="Stop"
                    size="xs"
                />
            </div>
        </div>
    );
};

// --- Component: Tutor Session Modal ---
const TutorSessionModal = ({ 
    task, 
    studentName, 
    orgName,
    onClose, 
    history, 
    onSendMessage,
    userLanguage
}: { 
    task: HomeworkTask, 
    studentName: string, 
    orgName: string,
    onClose: () => void, 
    history: Interaction[], 
    onSendMessage: (text?: string, triggerType?: 'question' | 'initial' | 'concept' | 'example' | 'resources') => void,
    userLanguage: string
}) => {
    const [input, setInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [history, isSending]);

    // Initial Trigger if empty (Level 1: Hint/Nudge)
    useEffect(() => {
        if (history.length === 0) {
            setIsSending(true);
            onSendMessage(undefined, 'initial');
            // Mock delay for UX since parent component handles async state updates
            setTimeout(() => setIsSending(false), 2000); 
        }
    }, []);

    const handleSend = async () => {
        if (!input.trim()) return;
        setIsSending(true);
        await onSendMessage(input, 'question');
        setInput('');
        setIsSending(false);
    };

    const handleDownload = () => {
        const content = history.map(h => 
            `[${new Date(h.timestamp).toLocaleTimeString()}] ${h.isStudentInput ? studentName : 'AI Tutor'}:\n${h.isStudentInput ? h.prompt + '\nResponse: ' + h.response : h.response}\n`
        ).join('\n----------------------------------------\n');
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Tutor_Session_${task.subject}_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Determine Logic State based on history content
    const hasConcept = history.some(h => h.prompt && h.prompt.toLowerCase().includes("explain the underlying concept"));
    const hasExample = history.some(h => h.prompt && h.prompt.toLowerCase().includes("give me a similar example"));
    const hasResources = history.some(h => h.prompt && h.prompt.toLowerCase().includes("find 2-3 helpful external resources"));
    
    // Session termination logic: Limit to 4 specific questions OR if resources have been provided
    const manualQuestionsCount = history.filter(h => h.isStudentInput).length;
    const isSessionEnded = hasResources || manualQuestionsCount >= 4;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fade-in backdrop-blur-sm">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-gray-200">
                {/* Header */}
                <div className="bg-indigo-700 p-4 text-white flex justify-between items-center shadow-md z-10">
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div className="bg-white/20 p-2 rounded-full flex-shrink-0">
                            <span className="material-icons-round text-2xl">school</span>
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-lg truncate">AI Tutor of {orgName} by EduGravity</h3>
                            <p className="text-xs opacity-90 text-indigo-100 truncate">{task.subject}: {task.description}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button 
                            onClick={handleDownload} 
                            className="p-2 hover:bg-white/20 rounded-full transition-colors flex items-center justify-center"
                            title="Download Conversation"
                        >
                            <span className="material-icons-round">download</span>
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors flex items-center justify-center">
                            <span className="material-icons-round">close</span>
                        </button>
                    </div>
                </div>

                {/* Real-time Brain Power Meter & Preservation Alert */}
                <div className="bg-gradient-to-r from-amber-50/90 via-indigo-50/80 to-emerald-50/90 px-4 py-2.5 border-b border-indigo-100 flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="p-1 rounded-md bg-white border border-indigo-200 text-indigo-700 shadow-2xs flex-shrink-0">
                            <span className="material-icons-round text-sm">psychology</span>
                        </span>
                        <div className="truncate">
                            <span className="font-bold text-gray-800">
                                {history.length <= 1 ? 'Spark Stage' : `AI Step ${history.length} of 4`}
                            </span>
                            <span className="text-gray-600 hidden sm:inline ml-1.5">
                                • {history.length <= 1 ? 'Asking fewer hints preserves 80-100% Brain Power!' : 'Try solving now to keep your Brain Score higher!'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-gray-500 text-[11px] font-medium hidden xs:inline">Task Brain Score:</span>
                        <span className={`px-2.5 py-0.5 rounded-full font-bold text-xs border flex items-center gap-1 ${
                            history.length === 0 
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300' 
                                : history.length === 1 
                                    ? 'bg-blue-100 text-blue-800 border-blue-300' 
                                    : history.length === 2 
                                        ? 'bg-amber-100 text-amber-800 border-amber-300' 
                                        : 'bg-purple-100 text-purple-800 border-purple-300'
                        }`}>
                            <span>{history.length === 0 ? '100%' : history.length === 1 ? '80%' : history.length === 2 ? '50%' : '25%'}</span>
                            <span className="text-[10px] opacity-80 font-normal">({history.length} {history.length === 1 ? 'Hint' : 'Hints'})</span>
                        </span>
                    </div>
                </div>

                {/* Chat Area */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-2">
                    {history.length === 0 && isSending && (
                        <div className="flex justify-start animate-pulse">
                            <div className="bg-white p-4 rounded-2xl rounded-tl-none border shadow-sm text-gray-500 text-sm">
                                Analyzing task...
                            </div>
                        </div>
                    )}
                    
                    {history.map((msg, idx) => (
                        <React.Fragment key={idx}>
                            {/* If it's a student input, show their question first */}
                            {msg.isStudentInput && (
                                <TutorBubble 
                                    text={msg.prompt} 
                                    isStudent={true} 
                                    studentName={studentName} 
                                    userLanguage={userLanguage} 
                                />
                            )}

                            {/* Show AI Response (always shown for both student input and button clicks) */}
                            <TutorBubble 
                                text={msg.response} 
                                isStudent={false} 
                                userLanguage={userLanguage} 
                            />
                        </React.Fragment>
                    ))}
                    
                    {isSending && history.length > 0 && (
                        <div className="flex justify-start items-end">
                             <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mb-1">
                                <span className="material-icons-round text-indigo-600 text-sm">smart_toy</span>
                            </div>
                            <div className="bg-gray-200 p-3 rounded-2xl rounded-bl-none flex gap-1">
                                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-75"></div>
                                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-150"></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer / Input Area */}
                {isSessionEnded ? (
                    <div className="p-6 bg-green-50 border-t border-green-100 text-center animate-fade-in">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="material-icons-round text-3xl text-green-600">verified</span>
                        </div>
                        <p className="text-green-800 font-bold text-lg mb-2">Great effort!</p>
                        <p className="text-green-700 text-sm max-w-sm mx-auto">
                            You've done great exploring this topic! For more help, please discuss with your teacher or read your book. Keep up the good work!
                        </p>
                    </div>
                ) : (
                    <div className="p-4 bg-white border-t border-gray-100">
                        {/* Independence Guidance Banner */}
                        <div className="mb-3 px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 text-[11px] text-indigo-900 flex items-center justify-between">
                            <span className="flex items-center gap-1.5">
                                <span className="material-icons-round text-sm text-indigo-600">psychology</span>
                                <span><strong>Brain Score Ladder:</strong> 0 AI: 100% • 1 Hint: 80% • 2 Hints: 50% • 3+ Hints: 25%</span>
                            </span>
                            <span className="font-bold text-indigo-700 bg-white px-2 py-0.5 rounded shadow-2xs text-[10px] hidden sm:inline">More Brain, Less AI</span>
                        </div>

                        {/* Level Based Buttons */}
                        {!isSending && (
                            <div className="mb-3 space-y-2">
                                {!hasConcept && (
                                    <button 
                                        onClick={() => onSendMessage(undefined, 'concept')} 
                                        className="w-full py-3 bg-purple-50 text-purple-700 font-bold rounded-xl border border-purple-200 hover:bg-purple-100 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <span className="material-icons-round">lightbulb</span>
                                        Explain me the concept
                                    </button>
                                )}
                                {hasConcept && !hasExample && (
                                    <button 
                                        onClick={() => onSendMessage(undefined, 'example')} 
                                        className="w-full py-3 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-200 hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <span className="material-icons-round">draw</span>
                                        Give me an example
                                    </button>
                                )}
                                {hasExample && !hasResources && (
                                    <button 
                                        onClick={() => onSendMessage(undefined, 'resources')} 
                                        className="w-full py-3 bg-teal-50 text-teal-700 font-bold rounded-xl border border-teal-200 hover:bg-teal-100 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <span className="material-icons-round">menu_book</span>
                                        Provide me resources
                                    </button>
                                )}
                            </div>
                        )}
                        
                        {/* Specific Question Input */}
                        <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-xl border border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                            <input 
                                type="text" 
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                placeholder="Ask a specific question..."
                                className="flex-1 p-2 bg-transparent border-none outline-none text-sm text-gray-800 placeholder-gray-500"
                                disabled={isSending}
                            />
                            <button 
                                onClick={handleSend}
                                disabled={!input.trim() || isSending}
                                className="p-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50 hover:bg-indigo-700 transition-colors shadow-sm"
                            >
                                <span className="material-icons-round text-xl">send</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Component: Homework Calendar ---
const HomeworkCalendar = ({ 
    homeworks, 
    selectedDate, 
    onSelectDate, 
    minDate 
}: { 
    homeworks: Homework[], 
    selectedDate: string, 
    onSelectDate: (date: string) => void,
    minDate: Date
}) => {
    const [viewingDate, setViewingDate] = useState(new Date(selectedDate));

    // Group homeworks by date for the indicators
    const homeworksByDate = useMemo(() => {
        const map: Record<string, string[]> = {};
        homeworks.forEach(hw => {
            if (!map[hw.date]) map[hw.date] = [];
            // Collect unique subjects for this date
            hw.tasks.forEach(t => {
                if (!map[hw.date].includes(t.subject)) {
                    map[hw.date].push(t.subject);
                }
            });
        });
        return map;
    }, [homeworks]);

    const changeMonth = (offset: number) => {
        const newDate = new Date(viewingDate);
        newDate.setMonth(newDate.getMonth() + offset);
        setViewingDate(newDate);
    };

    const renderDays = () => {
        const year = viewingDate.getFullYear();
        const month = viewingDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay(); // 0 = Sun
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        // Empty slots for previous month
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="h-14"></div>);
        }

        // Days
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = dateStr === selectedDate;
            const subjects = homeworksByDate[dateStr] || [];
            const isBeforeMin = new Date(dateStr) < minDate;
            const isToday = dateStr === new Date().toISOString().split('T')[0];

            days.push(
                <button 
                    key={day} 
                    onClick={() => !isBeforeMin && onSelectDate(dateStr)}
                    disabled={isBeforeMin}
                    className={`h-14 flex flex-col items-center justify-start pt-1 rounded-lg transition-all relative
                        ${isSelected ? 'bg-indigo-600 text-white shadow-md' : 'hover:bg-gray-100 text-gray-700'}
                        ${isBeforeMin ? 'opacity-30 cursor-not-allowed' : ''}
                        ${isToday && !isSelected ? 'border border-indigo-200 bg-indigo-50' : ''}
                    `}
                >
                    <span className={`text-sm font-medium ${isSelected ? 'font-bold' : ''}`}>{day}</span>
                    
                    {/* Subject Indicators */}
                    <div className="flex gap-0.5 mt-1 flex-wrap justify-center max-w-[90%]">
                        {subjects.slice(0, 4).map((subj, idx) => (
                            <div 
                                key={idx} 
                                className={`w-1.5 h-1.5 rounded-full ${getSubjectColor(subj)} ${isSelected ? 'ring-1 ring-white' : ''}`} 
                                title={subj}
                            />
                        ))}
                        {subjects.length > 4 && (
                            <div className={`w-1.5 h-1.5 rounded-full bg-gray-400 ${isSelected ? 'ring-1 ring-white' : ''}`} />
                        )}
                    </div>
                </button>
            );
        }
        return days;
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-gray-100 rounded-full">
                    <span className="material-icons-round text-gray-500">chevron_left</span>
                </button>
                <h4 className="font-bold text-gray-800">{viewingDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</h4>
                <button onClick={() => changeMonth(1)} className="p-1 hover:bg-gray-100 rounded-full">
                    <span className="material-icons-round text-gray-500">chevron_right</span>
                </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['S','M','T','W','T','F','S'].map((d, i) => (
                    <span key={i} className="text-xs font-bold text-gray-400">{d}</span>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {renderDays()}
            </div>
            <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-400"></div> Math
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-400"></div> Sci
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-yellow-400"></div> Eng
                </div>
            </div>
        </div>
    );
};

interface TaskItemProps {
    task: HomeworkTask;
    studentStatus?: StudentHomeworkStatus;
    onUpdateStatus?: (studentId: string, homeworkId: string, taskId: string, isComplete: boolean) => void;
    onStuck?: (task: HomeworkTask) => void;
    studentId?: string;
    homeworkId: string;
    t: (key: string) => string;
    userLanguage: string;
    tutorLogs?: TutorLog[];
}

export const TaskItem: React.FC<TaskItemProps> = ({ task, studentStatus, onUpdateStatus, onStuck, studentId, homeworkId, t, userLanguage, tutorLogs }) => {
    const isCompleted = studentStatus?.completedTaskIds.includes(task.id);
    const [translatedDesc, setTranslatedDesc] = useState<string | null>(null);
    const [showTranslated, setShowTranslated] = useState(false);
    const [translating, setTranslating] = useState(false);

    const taskLog = tutorLogs?.find(l => l.studentId === studentId && l.homeworkId === homeworkId && l.taskId === task.id);
    const brainScore = getTaskBrainScore(taskLog);

    useEffect(() => {
        setTranslatedDesc(null);
        setShowTranslated(false);
    }, [userLanguage, task.id, task.description]);

    const toggleTranslate = async () => {
        if (!translatedDesc) {
            setTranslating(true);
            try {
                const res = await translateContent(task.description, userLanguage);
                setTranslatedDesc(res);
                setShowTranslated(true);
            } catch (e) {
                console.error("Translation failed", e);
            } finally {
                setTranslating(false);
            }
        } else {
            setShowTranslated(!showTranslated);
        }
    };
    
    const canTranslate = Boolean(task.description && task.description.trim().length > 0);

    return (
        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/90 flex justify-between items-start hover:border-indigo-200 transition-colors">
            <div className="flex-1 pr-2 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className={`w-2 h-2 rounded-full ${getSubjectColor(task.subject)}`}></div>
                    <p className="font-bold text-sm text-gray-800 uppercase tracking-wide">{task.subject}</p>
                    
                    {/* Brain Power Status Tag */}
                    {isCompleted ? (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                            brainScore.hintsUsed === 0 
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300' 
                                : brainScore.hintsUsed === 1
                                    ? 'bg-blue-50 text-blue-800 border-blue-300'
                                    : brainScore.hintsUsed === 2
                                        ? 'bg-amber-50 text-amber-800 border-amber-300'
                                        : 'bg-purple-50 text-purple-800 border-purple-300'
                        }`}>
                            <span className="material-icons-round !text-[12px]">{brainScore.icon}</span>
                            <span>{brainScore.label}</span>
                        </span>
                    ) : brainScore.hintsUsed > 0 ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 flex items-center gap-1">
                            <span className="material-icons-round !text-[12px]">lightbulb</span>
                            <span>{brainScore.hintsUsed} AI Hint{brainScore.hintsUsed > 1 ? 's' : ''} Asked • Brain Score: {brainScore.score}%</span>
                        </span>
                    ) : (
                        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50/70 border border-emerald-200/60 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <span className="material-icons-round !text-[11px] text-emerald-600">psychology</span>
                            <span>100% Brain Power (0 AI Used)</span>
                        </span>
                    )}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                    {showTranslated && translatedDesc ? translatedDesc : task.description}
                </p>
                <div className="flex items-center gap-3 mt-2">
                    {canTranslate && (
                        <button 
                            onClick={toggleTranslate} 
                            disabled={translating}
                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors"
                        >
                            <span className="material-icons-round text-[13px]">translate</span>
                            {translating ? (t('processing_translation') || 'Translating...') : (showTranslated ? (t('show_original') || 'Show Original') : (t('translate') || 'Translate'))}
                        </button>
                    )}
                    
                    <ListenButton
                        id={`hw-task-${task.id}`}
                        text={showTranslated && translatedDesc ? translatedDesc : task.description}
                        language={showTranslated ? userLanguage : 'en'}
                        variant="pill"
                        label={t('listen') || 'Listen'}
                        stopLabel={t('stop') || 'Stop'}
                        size="xs"
                    />
                </div>
                {task.estimatedTime && <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><span className="material-icons-round text-[10px]">schedule</span> {task.estimatedTime} mins</p>}
            </div>
            {studentId && onUpdateStatus && (
                <div className="flex flex-col gap-2 items-end flex-shrink-0">
                     <button 
                        onClick={() => onUpdateStatus(studentId, homeworkId, task.id, !isCompleted)} 
                        className={`p-2 rounded-full border transition-all ${isCompleted ? 'bg-green-500 text-white border-green-500 shadow-md' : 'bg-white text-gray-300 border-gray-200 hover:border-green-400 hover:text-green-400'}`}
                        title={isCompleted ? "Mark as incomplete" : "Mark as complete"}
                    >
                        <span className="material-icons-round text-lg">check</span>
                    </button>
                    {!isCompleted && onStuck && (
                        <div className="flex flex-col items-end gap-0.5">
                            <button 
                                onClick={() => onStuck(task)} 
                                className="text-[10px] font-bold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-200 hover:bg-orange-100 transition-colors flex items-center gap-1 whitespace-nowrap shadow-2xs"
                            >
                                <span className="material-icons-round text-[12px]">help_outline</span>
                                I'm Stuck?
                            </button>
                            <span className="text-[9px] text-gray-400 font-medium">Use AI wisely</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface HomeworkDigestCardProps {
    homework: Homework | undefined;
    studentStatus?: StudentHomeworkStatus;
    onUpdateStatus?: (studentId: string, homeworkId: string, taskId: string, isComplete: boolean) => void;
    onStuck?: (task: HomeworkTask) => void;
    studentId?: string;
    completionPercentage: number;
    t: (key: string) => string;
    userLanguage: string;
    selectedDate?: string;
    onJumpToDate?: (date: string) => void;
    nextAvailableDate?: string;
    tutorLogs?: TutorLog[];
}

export const HomeworkDigestCard: React.FC<HomeworkDigestCardProps> = ({ 
    homework, 
    studentStatus, 
    onUpdateStatus, 
    onStuck, 
    studentId, 
    completionPercentage, 
    t, 
    userLanguage,
    selectedDate,
    onJumpToDate,
    nextAvailableDate,
    tutorLogs
}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const completedCount = studentStatus?.completedTaskIds.length || 0;
    const totalTasks = homework?.tasks.length || 0;
    const [viewingImage, setViewingImage] = useState<string | null>(null);
    const [taskFilter, setTaskFilter] = useState<'all' | 'pending' | 'completed'>('all');

    const dayBrainMetrics = useMemo(() => {
        return computeDayBrainMetrics(homework, studentStatus, tutorLogs || [], studentId || '');
    }, [homework, studentStatus, tutorLogs, studentId]);

    const todayStr = new Date().toISOString().split('T')[0];

    const handlePrint = () => {
        if(cardRef.current) {
            cardRef.current.classList.add('printable');
            window.print();
            cardRef.current.classList.remove('printable');
        }
    };

    // Filter tasks based on selected filter pill
    const filteredTasks = useMemo(() => {
        if (!homework) return [];
        if (taskFilter === 'completed') {
            return homework.tasks.filter(task => studentStatus?.completedTaskIds.includes(task.id));
        }
        if (taskFilter === 'pending') {
            return homework.tasks.filter(task => !studentStatus?.completedTaskIds.includes(task.id));
        }
        return homework.tasks;
    }, [homework, taskFilter, studentStatus]);
    
    return (
        <div ref={cardRef} className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-4 sm:p-5 animate-slide-up">
            <div className="flex justify-between items-start mb-3">
                <div className="min-w-0 pr-2">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">
                            {t('homework')}
                        </span>
                        {homework && (
                            <span className="text-xs text-gray-400 font-medium">
                                {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'}
                            </span>
                        )}
                    </div>
                    <h3 className="font-bold text-base sm:text-lg text-gray-900 truncate">
                        {homework ? new Date(homework.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }) : (selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }) : 'Select a date')}
                    </h3>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={handlePrint} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg no-print transition-colors" title="Print Homework">
                        <span className="material-icons-round text-xl">print</span>
                    </button>
                    <div className="w-14 h-14 flex items-center justify-center">
                        <ProgressRing percentage={completionPercentage} />
                    </div>
                </div>
            </div>

            {homework ? (
                <div className="space-y-4">
                    {/* Linear Progress Bar with Status */}
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-1.5">
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-gray-700">
                                {completedCount === totalTasks && totalTasks > 0 ? (
                                    <span className="text-emerald-700 font-bold flex items-center gap-1">
                                        <span className="material-icons-round !text-sm text-emerald-600">check_circle</span>
                                        All Tasks Completed!
                                    </span>
                                ) : (
                                    `${completedCount} of ${totalTasks} completed`
                                )}
                            </span>
                            <span className="font-bold text-indigo-600">{Math.round(completionPercentage)}%</span>
                        </div>
                        <div className="w-full bg-gray-200/80 h-2 rounded-full overflow-hidden">
                            <div 
                                className={`h-full transition-all duration-500 rounded-full ${completionPercentage === 100 ? 'bg-emerald-500' : 'bg-indigo-600'}`}
                                style={{ width: `${Math.min(100, Math.max(0, completionPercentage))}%` }}
                            />
                        </div>
                    </div>

                    {/* Daily Brain Power & Independent Thinking Meter */}
                    <div className="bg-gradient-to-r from-emerald-50/70 via-indigo-50/50 to-amber-50/70 p-3 rounded-xl border border-emerald-200/80 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="p-1.5 bg-white rounded-lg shadow-2xs border border-emerald-200 text-emerald-700">
                                    <span className="material-icons-round !text-base">psychology</span>
                                </span>
                                <div>
                                    <h4 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                                        Daily Brain Power
                                        <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                            More Brain, Less AI
                                        </span>
                                    </h4>
                                    <p className="text-[11px] text-gray-600">
                                        {dayBrainMetrics.summaryTag}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-base font-black text-gray-900">{dayBrainMetrics.averageScore}%</span>
                                <span className="block text-[10px] text-gray-500 font-medium">Brain Score</span>
                            </div>
                        </div>

                        {/* Breakdown status */}
                        <div className="flex items-center gap-2 pt-1 border-t border-emerald-100 text-[11px] flex-wrap">
                            <span className="text-emerald-800 font-medium flex items-center gap-1">
                                <span className="material-icons-round !text-xs text-emerald-600">verified</span>
                                {dayBrainMetrics.pureBrainTasks} Tasks with 0 AI
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-600 flex items-center gap-1">
                                <span className="material-icons-round !text-xs text-indigo-500">smart_toy</span>
                                {dayBrainMetrics.hintsUsedTotal} AI Hints Used
                            </span>
                            {dayBrainMetrics.allPureBrain && totalTasks > 0 && completedCount > 0 && (
                                <>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-amber-700 font-bold flex items-center gap-0.5">
                                        <span className="material-icons-round !text-xs text-amber-500">workspace_premium</span>
                                        100% Brain Power Day!
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-1.5 flex-wrap pt-1">
                        <button
                            type="button"
                            onClick={() => setTaskFilter('all')}
                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                taskFilter === 'all'
                                    ? 'bg-indigo-600 text-white shadow-xs'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            All ({totalTasks})
                        </button>
                        <button
                            type="button"
                            onClick={() => setTaskFilter('pending')}
                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                taskFilter === 'pending'
                                    ? 'bg-amber-600 text-white shadow-xs'
                                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                            }`}
                        >
                            Pending ({totalTasks - completedCount})
                        </button>
                        <button
                            type="button"
                            onClick={() => setTaskFilter('completed')}
                            className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                taskFilter === 'completed'
                                    ? 'bg-emerald-600 text-white shadow-xs'
                                    : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            }`}
                        >
                            Completed ({completedCount})
                        </button>
                    </div>

                    {/* Attachments / Whiteboard Section */}
                    {homework.attachments && homework.attachments.length > 0 && (
                        <div className="pt-1">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                <span className="material-icons-round !text-sm text-indigo-500">attach_file</span>
                                Whiteboard & Reference Material
                            </p>
                            <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-hide">
                                {homework.attachments.map((url, idx) => {
                                    const type = getAttachmentType(url);
                                    const filename = getAttachmentFileName(url);

                                    if (type === 'image') {
                                        return (
                                            <div 
                                                key={idx}
                                                onClick={() => setViewingImage(url)}
                                                className="relative group cursor-pointer flex-shrink-0 rounded-xl overflow-hidden border border-gray-200 shadow-2xs hover:shadow-md transition-all"
                                            >
                                                <img 
                                                    src={url} 
                                                    alt="Whiteboard preview" 
                                                    className="h-24 w-36 object-cover group-hover:scale-105 transition-transform duration-200"
                                                />
                                                <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <span className="material-icons-round text-white text-xl">zoom_in</span>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <a 
                                            key={idx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="relative group flex-shrink-0 h-24 w-36 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 hover:bg-indigo-50/50 p-2.5 flex flex-col justify-between shadow-2xs hover:shadow-md transition-all text-left"
                                            title={filename}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className={`material-icons-round text-2xl ${type === 'pdf' ? 'text-red-500' : 'text-blue-500'}`}>
                                                    {type === 'pdf' ? 'picture_as_pdf' : 'description'}
                                                </span>
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${type === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {type === 'pdf' ? 'PDF' : 'DOC'}
                                                </span>
                                            </div>
                                            <p className="text-[11px] font-medium text-gray-700 truncate group-hover:text-indigo-600">
                                                {filename}
                                            </p>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Task Checklist Items */}
                    <div className="space-y-2.5">
                        {filteredTasks.map(task => (
                            <TaskItem 
                                key={task.id} 
                                task={task} 
                                studentStatus={studentStatus} 
                                onUpdateStatus={onUpdateStatus} 
                                onStuck={onStuck} 
                                studentId={studentId} 
                                homeworkId={homework.id}
                                t={t}
                                userLanguage={userLanguage}
                                tutorLogs={tutorLogs}
                            />
                        ))}

                        {/* Filtered Empty State */}
                        {filteredTasks.length === 0 && (
                            taskFilter === 'pending' ? (
                                <div className="p-6 bg-emerald-50/70 rounded-xl border border-emerald-200/80 text-center space-y-1">
                                    <span className="material-icons-round text-3xl text-emerald-600">celebration</span>
                                    <p className="text-sm font-bold text-emerald-900">All caught up!</p>
                                    <p className="text-xs text-emerald-700">Every task for this date has been completed.</p>
                                </div>
                            ) : taskFilter === 'completed' ? (
                                <div className="p-6 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center space-y-1">
                                    <span className="material-icons-round text-3xl text-gray-400">pending_actions</span>
                                    <p className="text-sm font-bold text-gray-700">No tasks completed yet</p>
                                    <p className="text-xs text-gray-500">Tap the checkmark next to each task when done.</p>
                                </div>
                            ) : (
                                <div className="p-6 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center text-gray-500 text-xs">
                                    No tasks available for this assignment.
                                </div>
                            )
                        )}
                    </div>
                    
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100 text-xs">
                        <span className="text-gray-400">Assignment ID: {homework.id.slice(-6)}</span>
                        <p className={`font-bold ${completedCount === totalTasks && totalTasks > 0 ? 'text-emerald-600' : 'text-gray-700'}`}>
                            {t('completed')}: {completedCount} / {totalTasks}
                        </p>
                    </div>
                </div>
            ) : (
                /* Empty state when no homework is posted on selected date */
                <div className="text-center py-10 px-4 bg-gray-50/70 rounded-xl border border-dashed border-gray-200 space-y-3">
                    <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto">
                        <span className="material-icons-round text-2xl">event_available</span>
                    </div>
                    <div>
                        <h4 className="font-bold text-sm sm:text-base text-gray-800">
                            {t('no_homework_posted') || 'No Homework Assigned'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                            No assignments or tasks are due on this date.
                        </p>
                    </div>
                    <div className="flex items-center justify-center gap-2 pt-1 flex-wrap">
                        {selectedDate && selectedDate !== todayStr && onJumpToDate && (
                            <button
                                type="button"
                                onClick={() => onJumpToDate(todayStr)}
                                className="px-3 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                            >
                                <span className="material-icons-round !text-sm">today</span>
                                Jump to Today
                            </button>
                        )}
                        {nextAvailableDate && nextAvailableDate !== selectedDate && onJumpToDate && (
                            <button
                                type="button"
                                onClick={() => onJumpToDate(nextAvailableDate)}
                                className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-1 shadow-2xs cursor-pointer"
                            >
                                <span className="material-icons-round !text-sm text-indigo-600">arrow_forward</span>
                                Next Assignment ({new Date(nextAvailableDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Simple Image Modal for Viewing */}
            {viewingImage && (
                <div className="fixed inset-0 z-60 bg-black/90 flex items-center justify-center p-4 animate-fade-in" onClick={() => setViewingImage(null)}>
                    <img src={viewingImage} className="max-w-full max-h-full rounded-lg shadow-2xl" alt="Full view" />
                    <button className="absolute top-4 right-4 text-white bg-white/20 p-2 rounded-full hover:bg-white/40 transition-colors">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>
            )}
        </div>
    );
};

// --- Component: Homework Details & Stuck Reports Modal ---
export const HomeworkDetailsModal = ({
    homework,
    classStudents,
    studentHomeworkStatuses,
    stuckLogs,
    tutorLogs,
    onClose,
    t,
    initialTab = 'stuck'
}: {
    homework: Homework;
    classStudents: StudentConnection[];
    studentHomeworkStatuses: StudentHomeworkStatus[];
    stuckLogs: StuckLog[];
    tutorLogs: TutorLog[];
    onClose: () => void;
    t: (key: string) => string;
    initialTab?: 'stuck' | 'roster' | 'tasks';
}) => {
    const [activeTab, setActiveTab] = useState<'stuck' | 'roster' | 'tasks'>(initialTab);
    const [searchQuery, setSearchQuery] = useState('');
    const [rosterFilter, setRosterFilter] = useState<'all' | 'completed' | 'incomplete' | 'stuck'>('all');
    const [expandedTutorLogId, setExpandedTutorLogId] = useState<string | null>(null);
    const [viewingAttachment, setViewingAttachment] = useState<string | null>(null);

    const hwStuckLogs = stuckLogs.filter(l => l.homeworkId === homework.id);
    const totalStuckCount = hwStuckLogs.length;

    const completedStudents = classStudents.filter(s => {
        const status = studentHomeworkStatuses.find(st => st.homeworkId === homework.id && st.studentId === s.id);
        return status && status.completedTaskIds.length === homework.tasks.length && homework.tasks.length > 0;
    });

    const completionRate = classStudents.length > 0 ? Math.round((completedStudents.length / classStudents.length) * 100) : 0;

    // Filtered students for Roster tab
    const filteredStudents = classStudents.filter(student => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            if (!student.childName.toLowerCase().includes(query)) return false;
        }
        const status = studentHomeworkStatuses.find(st => st.homeworkId === homework.id && st.studentId === student.id);
        const isCompleted = status && status.completedTaskIds.length === homework.tasks.length && homework.tasks.length > 0;
        const isStuck = hwStuckLogs.some(l => l.studentId === student.id);

        if (rosterFilter === 'completed') return isCompleted;
        if (rosterFilter === 'incomplete') return !isCompleted;
        if (rosterFilter === 'stuck') return isStuck;
        return true;
    });

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4 backdrop-blur-xs animate-fade-in">
            <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100">
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-indigo-700 via-indigo-800 to-indigo-900 p-5 text-white flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="material-icons-round text-indigo-200 text-lg">assignment</span>
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-200">Homework Details</span>
                        </div>
                        <h3 className="font-bold text-xl text-white">
                            {new Date(homework.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3">
                            <span className="bg-white/15 px-2.5 py-1 rounded-full text-xs font-semibold text-white backdrop-blur-xs flex items-center gap-1">
                                <span className="material-icons-round text-xs">people</span>
                                {classStudents.length} Students
                            </span>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                                completionRate === 100 ? 'bg-emerald-500/30 text-emerald-200 border border-emerald-400/40' : 'bg-white/15 text-white'
                            }`}>
                                <span className="material-icons-round text-xs">check_circle</span>
                                {completedStudents.length} / {classStudents.length} Complete ({completionRate}%)
                            </span>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                                totalStuckCount > 0 ? 'bg-rose-500 text-white shadow-xs animate-pulse' : 'bg-white/15 text-indigo-100'
                            }`}>
                                <span className="material-icons-round text-xs">{totalStuckCount > 0 ? 'error_outline' : 'sentiment_satisfied'}</span>
                                {totalStuckCount} Stuck {totalStuckCount === 1 ? 'Report' : 'Reports'}
                            </span>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                        title="Close"
                    >
                        <span className="material-icons-round text-2xl">close</span>
                    </button>
                </div>

                {/* Tabs Navigation */}
                <div className="flex border-b border-gray-200 bg-gray-50/80 px-4 pt-2 gap-2 overflow-x-auto scrollbar-hide">
                    <button
                        onClick={() => setActiveTab('stuck')}
                        className={`pb-3 px-3.5 text-xs sm:text-sm font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap ${
                            activeTab === 'stuck'
                                ? 'border-rose-600 text-rose-700 bg-white rounded-t-lg shadow-2xs'
                                : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span className="material-icons-round text-base text-rose-500">help_outline</span>
                        <span>I'm Stuck Reports</span>
                        {totalStuckCount > 0 && (
                            <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-rose-200 ml-1">
                                {totalStuckCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('roster')}
                        className={`pb-3 px-3.5 text-xs sm:text-sm font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap ${
                            activeTab === 'roster'
                                ? 'border-indigo-600 text-indigo-700 bg-white rounded-t-lg shadow-2xs'
                                : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span className="material-icons-round text-base text-indigo-500">groups</span>
                        <span>Student Progress ({classStudents.length})</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={`pb-3 px-3.5 text-xs sm:text-sm font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap ${
                            activeTab === 'tasks'
                                ? 'border-indigo-600 text-indigo-700 bg-white rounded-t-lg shadow-2xs'
                                : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span className="material-icons-round text-base text-indigo-500">format_list_bulleted</span>
                        <span>Tasks & Attachments ({homework.tasks.length})</span>
                    </button>
                </div>

                {/* Tab Content Body */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 bg-gray-50 space-y-4">
                    {/* TAB 1: I'M STUCK REPORTS */}
                    {activeTab === 'stuck' && (
                        <div className="space-y-4">
                            {totalStuckCount === 0 ? (
                                <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 p-8 shadow-xs">
                                    <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3 text-emerald-500">
                                        <span className="material-icons-round text-3xl">sentiment_satisfied_alt</span>
                                    </div>
                                    <h4 className="font-bold text-gray-800 text-base">No Stuck Reports</h4>
                                    <p className="text-sm text-gray-500 max-w-sm mx-auto mt-1">
                                        None of the students in this class flagged "I'm Stuck" on this homework assignment.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-start gap-3">
                                        <span className="material-icons-round text-amber-600 text-xl flex-shrink-0 mt-0.5">info</span>
                                        <p className="text-xs sm:text-sm text-amber-900 leading-relaxed">
                                            The following students reported needing assistance. Review their AI Tutor transcripts below to see their questions and provide targeted teacher guidance.
                                        </p>
                                    </div>

                                    {homework.tasks.map(task => {
                                        const taskStuckLogs = hwStuckLogs.filter(l => l.taskId === task.id);
                                        if (taskStuckLogs.length === 0) return null;

                                        return (
                                            <div key={task.id} className="bg-white rounded-2xl border border-rose-100 shadow-xs p-4 sm:p-5 space-y-3">
                                                <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-gray-100">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-2.5 h-2.5 rounded-full ${getSubjectColor(task.subject)}`}></span>
                                                        <span className="font-bold text-sm text-gray-900 uppercase tracking-wide">{task.subject}</span>
                                                        <span className="text-xs text-gray-400 font-medium">• {task.description}</span>
                                                    </div>
                                                    <span className="bg-rose-100 text-rose-700 text-xs font-bold px-2.5 py-0.5 rounded-full border border-rose-200">
                                                        {taskStuckLogs.length} {taskStuckLogs.length === 1 ? 'student stuck' : 'students stuck'}
                                                    </span>
                                                </div>

                                                <div className="space-y-2.5 pt-1">
                                                    {taskStuckLogs.map(log => {
                                                        const student = classStudents.find(s => s.id === log.studentId);
                                                        const studentStatus = studentHomeworkStatuses.find(st => st.homeworkId === homework.id && st.studentId === log.studentId);
                                                        const isNowComplete = studentStatus?.completedTaskIds.includes(task.id);
                                                        const tutorLog = tutorLogs.find(tl => tl.studentId === log.studentId && tl.homeworkId === homework.id && tl.taskId === task.id);
                                                        const isTutorExpanded = expandedTutorLogId === `${log.studentId}_${task.id}`;

                                                        return (
                                                            <div key={log.id} className="bg-gray-50 rounded-xl p-3.5 border border-gray-200/80 hover:border-indigo-200 transition-colors">
                                                                <div className="flex items-center justify-between flex-wrap gap-2">
                                                                    <div className="flex items-center gap-3 min-w-0">
                                                                        <Avatar photoUrl={student?.photoUrl} name={student?.childName || 'Student'} size="sm" />
                                                                        <div>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="font-bold text-sm text-gray-900">{student?.childName || 'Student'}</span>
                                                                                {isNowComplete ? (
                                                                                    <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-md border border-emerald-200 flex items-center gap-0.5">
                                                                                        <span className="material-icons-round text-[11px]">check</span> Completed Later
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="bg-rose-100 text-rose-800 text-[10px] font-bold px-2 py-0.5 rounded-md border border-rose-200">
                                                                                        Still Pending
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <p className="text-[11px] text-gray-400 mt-0.5">
                                                                                Reported: {new Date(log.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    {tutorLog && tutorLog.interactions && tutorLog.interactions.length > 0 ? (
                                                                        <button
                                                                            onClick={() => setExpandedTutorLogId(isTutorExpanded ? null : `${log.studentId}_${task.id}`)}
                                                                            className={`text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-2xs ${
                                                                                isTutorExpanded
                                                                                    ? 'bg-indigo-600 text-white shadow-xs'
                                                                                    : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'
                                                                            }`}
                                                                        >
                                                                            <span className="material-icons-round text-sm">smart_toy</span>
                                                                            <span>{isTutorExpanded ? 'Hide AI Tutor Chat' : `View AI Tutor Chat (${tutorLog.interactions.length})`}</span>
                                                                            <span className="material-icons-round text-xs">{isTutorExpanded ? 'expand_less' : 'expand_more'}</span>
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-[11px] text-gray-400 italic bg-gray-100 px-2 py-1 rounded-md">
                                                                            No AI tutor chat session
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Expandable AI Tutor Session Viewer */}
                                                                {isTutorExpanded && tutorLog && tutorLog.interactions && (
                                                                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2 animate-fade-in">
                                                                        <div className="flex items-center justify-between text-xs text-gray-500 font-semibold mb-2">
                                                                            <span className="flex items-center gap-1 text-indigo-700">
                                                                                <span className="material-icons-round text-xs">forum</span>
                                                                                AI Tutor Session Transcript
                                                                            </span>
                                                                            <span>{tutorLog.interactions.length} interactions</span>
                                                                        </div>

                                                                        <div className="bg-white rounded-xl p-3 border border-indigo-100 space-y-3 max-h-60 overflow-y-auto">
                                                                            {tutorLog.interactions.map((interaction, idx) => (
                                                                                <div key={idx} className="space-y-1.5 text-xs">
                                                                                    {interaction.prompt && (
                                                                                        <div className="flex justify-end">
                                                                                            <div className="bg-indigo-600 text-white p-2.5 rounded-xl rounded-br-none max-w-[85%] shadow-2xs">
                                                                                                <p className="font-semibold text-[10px] text-indigo-200 mb-0.5">{student?.childName || 'Student'}:</p>
                                                                                                <p className="whitespace-pre-wrap">{interaction.prompt}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                    {interaction.response && (
                                                                                        <div className="flex justify-start">
                                                                                            <div className="bg-gray-100 text-gray-800 p-2.5 rounded-xl rounded-bl-none max-w-[85%] border border-gray-200 shadow-2xs">
                                                                                                <div className="flex items-center gap-1 text-indigo-600 font-semibold text-[10px] mb-0.5">
                                                                                                    <span className="material-icons-round text-[12px]">smart_toy</span>
                                                                                                    AI Tutor
                                                                                                </div>
                                                                                                <p className="whitespace-pre-wrap">{interaction.response}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 2: STUDENT ROSTER PROGRESS */}
                    {activeTab === 'roster' && (
                        <div className="space-y-3">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 bg-white p-3 rounded-xl border border-gray-200">
                                <div className="relative flex-1">
                                    <span className="material-icons-round absolute left-3 top-2.5 text-gray-400 text-sm">search</span>
                                    <input
                                        type="text"
                                        placeholder="Search student by name..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <button
                                        onClick={() => setRosterFilter('all')}
                                        className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${rosterFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        All ({classStudents.length})
                                    </button>
                                    <button
                                        onClick={() => setRosterFilter('completed')}
                                        className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${rosterFilter === 'completed' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                                    >
                                        Complete ({completedStudents.length})
                                    </button>
                                    <button
                                        onClick={() => setRosterFilter('incomplete')}
                                        className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${rosterFilter === 'incomplete' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                                    >
                                        Incomplete ({classStudents.length - completedStudents.length})
                                    </button>
                                    <button
                                        onClick={() => setRosterFilter('stuck')}
                                        className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${rosterFilter === 'stuck' ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                                    >
                                        Stuck ({classStudents.filter(s => hwStuckLogs.some(l => l.studentId === s.id)).length})
                                    </button>
                                </div>
                            </div>

                            {filteredStudents.length === 0 ? (
                                <div className="text-center py-10 bg-white rounded-xl border border-gray-100 text-gray-500">
                                    <p className="text-sm font-medium">No students match your criteria.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {filteredStudents.map(student => {
                                        const status = studentHomeworkStatuses.find(st => st.homeworkId === homework.id && st.studentId === student.id);
                                        const completedTaskIds = status?.completedTaskIds || [];
                                        const completedCount = completedTaskIds.length;
                                        const totalTasks = homework.tasks.length;
                                        const studentPercent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
                                        const isFullyComplete = completedCount === totalTasks && totalTasks > 0;
                                        const studentStuckLogs = hwStuckLogs.filter(l => l.studentId === student.id);
                                        const studentDayBrain = computeDayBrainMetrics(homework, status, tutorLogs, student.id);

                                        return (
                                            <div key={student.id} className="bg-white p-3.5 rounded-xl border border-gray-200/80 shadow-2xs hover:border-indigo-200 transition-all flex flex-col justify-between space-y-2.5">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <Avatar photoUrl={student.photoUrl} name={student.childName} size="sm" />
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-sm text-gray-900 truncate">{student.childName}</p>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <p className="text-[11px] text-gray-400">
                                                                    {completedCount} of {totalTasks} tasks ({studentPercent}%)
                                                                </p>
                                                                <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-md border flex items-center gap-0.5 ${
                                                                    studentDayBrain.allPureBrain && completedCount > 0
                                                                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                                                        : studentDayBrain.averageScore >= 80
                                                                            ? 'bg-blue-50 text-blue-800 border-blue-300'
                                                                            : 'bg-amber-50 text-amber-800 border-amber-300'
                                                                }`}>
                                                                    <span className="material-icons-round !text-[11px]">psychology</span>
                                                                    <span>{studentDayBrain.averageScore}% Brain Power</span>
                                                                    {studentDayBrain.allPureBrain && completedCount > 0 && (
                                                                        <span className="font-bold text-emerald-700">(0 AI)</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        {studentStuckLogs.length > 0 && (
                                                            <span className="bg-rose-100 text-rose-700 text-[10px] font-bold px-2 py-0.5 rounded-md border border-rose-200">
                                                                Stuck
                                                            </span>
                                                        )}
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                                                            isFullyComplete 
                                                                ? 'bg-emerald-100 text-emerald-800' 
                                                                : completedCount > 0 
                                                                    ? 'bg-amber-100 text-amber-800' 
                                                                    : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            {isFullyComplete ? 'Completed' : completedCount > 0 ? 'In Progress' : 'Not Started'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Task Checklist Breakdown */}
                                                <div className="space-y-1 bg-gray-50 p-2 rounded-lg text-xs">
                                                    {homework.tasks.map(task => {
                                                        const isTaskDone = completedTaskIds.includes(task.id);
                                                        const isTaskStuck = studentStuckLogs.some(l => l.taskId === task.id);
                                                        const studentTaskLog = tutorLogs.find(tl => tl.studentId === student.id && tl.homeworkId === homework.id && tl.taskId === task.id);
                                                        const studentTaskBrain = getTaskBrainScore(studentTaskLog);

                                                        return (
                                                            <div key={task.id} className="flex items-center justify-between gap-1.5">
                                                                <div className="flex items-center gap-1.5 truncate">
                                                                    <span className={`material-icons-round text-sm ${isTaskDone ? 'text-emerald-600' : 'text-gray-300'}`}>
                                                                        {isTaskDone ? 'check_circle' : 'radio_button_unchecked'}
                                                                    </span>
                                                                    <span className={`truncate ${isTaskDone ? 'text-gray-700 font-medium line-through opacity-70' : 'text-gray-800 font-medium'}`}>
                                                                        {task.subject}: {task.description}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                    {isTaskDone && (
                                                                        <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded border ${
                                                                            studentTaskBrain.hintsUsed === 0
                                                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                                                : 'bg-blue-50 text-blue-700 border-blue-200'
                                                                        }`}>
                                                                            {studentTaskBrain.hintsUsed === 0 ? '0 AI (100%)' : `${studentTaskBrain.hintsUsed} Hint (${studentTaskBrain.score}%)`}
                                                                        </span>
                                                                    )}
                                                                    {isTaskStuck && (
                                                                        <span className="text-[9px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
                                                                            Stuck
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 3: TASKS & ATTACHMENTS */}
                    {activeTab === 'tasks' && (
                        <div className="space-y-4">
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs space-y-3">
                                <h4 className="font-bold text-sm text-gray-800 uppercase tracking-wide">Tasks List ({homework.tasks.length})</h4>
                                <div className="space-y-2">
                                    {homework.tasks.map((task, idx) => (
                                        <div key={task.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-start justify-between gap-2">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`w-2 h-2 rounded-full ${getSubjectColor(task.subject)}`}></span>
                                                    <span className="font-bold text-xs text-gray-800 uppercase">{task.subject}</span>
                                                    {task.estimatedTime && (
                                                        <span className="text-[11px] text-gray-400">• {task.estimatedTime} mins</span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-700">{task.description}</p>
                                            </div>
                                            <span className="text-xs font-bold text-gray-400 bg-white px-2 py-1 rounded border">
                                                Task #{idx + 1}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {homework.attachments && homework.attachments.length > 0 && (
                                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs space-y-3">
                                    <h4 className="font-bold text-sm text-gray-800 uppercase tracking-wide">Attachments & Worksheets ({homework.attachments.length})</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {homework.attachments.map((url, idx) => {
                                            const type = getAttachmentType(url);
                                            const filename = getAttachmentFileName(url);

                                            if (type === 'image') {
                                                return (
                                                    <div 
                                                        key={idx}
                                                        onClick={() => setViewingAttachment(url)}
                                                        className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 shadow-2xs hover:shadow-md transition-all"
                                                    >
                                                        <img src={url} alt={`Attachment ${idx + 1}`} className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300" />
                                                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <span className="material-icons-round text-white text-2xl">zoom_in</span>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <a 
                                                    key={idx}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50 hover:bg-indigo-50/50 p-3 flex flex-col justify-between h-32 shadow-2xs hover:shadow-md transition-all text-left"
                                                    title={filename}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className={`material-icons-round text-3xl ${type === 'pdf' ? 'text-red-500' : 'text-blue-500'}`}>
                                                            {type === 'pdf' ? 'picture_as_pdf' : 'description'}
                                                        </span>
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${type === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {type === 'pdf' ? 'PDF' : 'DOC'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-800 line-clamp-2 group-hover:text-indigo-600">
                                                            {filename}
                                                        </p>
                                                        <span className="text-[10px] text-indigo-600 font-medium mt-1 flex items-center gap-1">
                                                            <span className="material-icons-round text-xs">open_in_new</span>
                                                            Open File
                                                        </span>
                                                    </div>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="p-3.5 bg-gray-50 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-xs transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Photo Attachment Zoom Modal */}
            {viewingAttachment && (
                <div 
                    className="fixed inset-0 z-60 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setViewingAttachment(null)}
                >
                    <img src={viewingAttachment} className="max-w-full max-h-full rounded-lg shadow-2xl" alt="Attachment Zoom" />
                    <button className="absolute top-4 right-4 text-white bg-white/20 p-2 rounded-full hover:bg-white/40 transition-colors">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export const PrincipalHomeworkView = ({ classStudents, allHomeworks, studentHomeworkStatuses, stuckLogs, t, tutorLogs }: {
    classStudents: StudentConnection[],
    allHomeworks: Homework[],
    studentHomeworkStatuses: StudentHomeworkStatus[],
    stuckLogs: StuckLog[],
    t: (key: string) => string,
    tutorLogs: TutorLog[]
}) => {
    const [selectedHomeworkForDetails, setSelectedHomeworkForDetails] = useState<Homework | null>(null);
    const [initialTabForModal, setInitialTabForModal] = useState<'stuck' | 'roster' | 'tasks'>('stuck');

    const handleOpenDetails = (hw: Homework, tab: 'stuck' | 'roster' | 'tasks' = 'stuck') => {
        setSelectedHomeworkForDetails(hw);
        setInitialTabForModal(tab);
    };

    return (
        <div className="p-4 space-y-4">
             {allHomeworks.length === 0 ? (
                 <p className="text-gray-500 text-center">{t('no_homework_posted')}</p>
             ) : (
                 allHomeworks.map(hw => {
                     const completedCount = studentHomeworkStatuses.filter(s => s.homeworkId === hw.id && s.completedTaskIds.length === hw.tasks.length).length;
                     const completionRate = classStudents.length > 0 ? (completedCount / classStudents.length) * 100 : 0;
                     const stuckCount = stuckLogs.filter(l => l.homeworkId === hw.id).length;
                     
                     return (
                         <div key={hw.id} className="bg-white rounded-xl shadow-sm border p-4 hover:border-indigo-200 transition-all">
                             <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-bold text-gray-800 text-base">{new Date(hw.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}</h4>
                                    <p className="text-xs text-gray-400 mt-0.5">{hw.tasks.length} {hw.tasks.length === 1 ? 'task' : 'tasks'} assigned</p>
                                </div>
                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${completionRate === 100 ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-yellow-100 text-yellow-700 border border-yellow-200'}`}>
                                    {Math.round(completionRate)}% Complete
                                </span>
                             </div>
                             <div className="space-y-1.5 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                                {hw.tasks.map(task => (
                                    <div key={task.id} className="flex items-center gap-2 text-sm text-gray-700">
                                        <span className={`w-2 h-2 rounded-full ${getSubjectColor(task.subject)} flex-shrink-0`}></span>
                                        <span className="font-semibold text-xs text-gray-800 uppercase">{task.subject}:</span>
                                        <span className="truncate text-xs">{task.description}</span>
                                    </div>
                                ))}
                             </div>
                             
                             <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                 <button 
                                     onClick={() => handleOpenDetails(hw, 'stuck')}
                                     className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1.5 ${
                                         stuckCount > 0 
                                             ? 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200' 
                                             : 'text-gray-500 hover:text-gray-700'
                                     }`}
                                     title="Click to view students reporting stuck"
                                 >
                                     <span className="material-icons-round text-sm text-rose-500">
                                         {stuckCount > 0 ? 'help_outline' : 'check_circle_outline'}
                                     </span>
                                     <span>
                                         <strong className={stuckCount > 0 ? 'text-rose-600 font-bold' : 'text-gray-700'}>{stuckCount}</strong> stuck reports
                                     </span>
                                 </button>
                                 <button 
                                     onClick={() => handleOpenDetails(hw, stuckCount > 0 ? 'stuck' : 'roster')}
                                     className="text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                                 >
                                     <span>View Details</span>
                                     <span className="material-icons-round text-sm">chevron_right</span>
                                 </button>
                             </div>
                         </div>
                     );
                 })
             )}

             {/* Homework Details Modal */}
             {selectedHomeworkForDetails && (
                 <HomeworkDetailsModal
                     homework={selectedHomeworkForDetails}
                     classStudents={classStudents}
                     studentHomeworkStatuses={studentHomeworkStatuses}
                     stuckLogs={stuckLogs}
                     tutorLogs={tutorLogs}
                     onClose={() => setSelectedHomeworkForDetails(null)}
                     t={t}
                     initialTab={initialTabForModal}
                 />
             )}
        </div>
    );
};

// --- Helper: Format & Parse Local Dates safely without DST jumps ---
const parseLocalDate = (dateStr: string) => {
    const parts = dateStr.split('-').map(Number);
    return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
};

const formatLocalDateStr = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// --- Component: Rolling 7-Day Homework Week Strip ---
interface HomeworkWeekStripProps {
    selectedDate: string;
    onSelectDate: (date: string) => void;
    homeworks: Homework[];
    studentStatuses: StudentHomeworkStatus[];
    studentId: string;
    t: (key: string) => string;
}

export const HomeworkWeekStrip: React.FC<HomeworkWeekStripProps> = ({
    selectedDate,
    onSelectDate,
    homeworks,
    studentStatuses,
    studentId,
    t
}) => {
    const todayStr = useMemo(() => formatLocalDateStr(new Date()), []);
    
    // Anchor date tracks which week we are browsing
    const [anchorDate, setAnchorDate] = useState<Date>(() => parseLocalDate(selectedDate || todayStr));

    // Keep anchorDate in sync if selectedDate changes drastically (e.g. jumped to another month)
    useEffect(() => {
        if (!selectedDate) return;
        const sel = parseLocalDate(selectedDate);
        const mon = new Date(anchorDate);
        const dayOfWeek = mon.getDay();
        const diff = mon.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        mon.setDate(diff);
        mon.setHours(0, 0, 0, 0);
        
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        sun.setHours(23, 59, 59, 999);

        if (sel < mon || sel > sun) {
            setAnchorDate(sel);
        }
    }, [selectedDate, anchorDate]);

    // Compute Monday–Sunday of current anchor week
    const { monday, sunday, weekDays } = useMemo(() => {
        const mon = new Date(anchorDate);
        const dayOfWeek = mon.getDay();
        const diff = mon.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        mon.setDate(diff);
        mon.setHours(12, 0, 0, 0);

        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);

        const days = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(mon);
            d.setDate(mon.getDate() + i);
            const dateStr = formatLocalDateStr(d);
            const dayNumber = d.getDate();
            const shortDay = d.toLocaleDateString(undefined, { weekday: 'short' });
            return { date: d, dateStr, dayNumber, shortDay };
        });

        return { monday: mon, sunday: sun, weekDays: days };
    }, [anchorDate]);

    const shiftWeek = (deltaWeeks: number) => {
        const next = new Date(anchorDate);
        next.setDate(anchorDate.getDate() + deltaWeeks * 7);
        setAnchorDate(next);
    };

    const jumpToToday = () => {
        const now = new Date();
        setAnchorDate(now);
        onSelectDate(todayStr);
    };

    const isCurrentWeek = useMemo(() => {
        const today = new Date();
        return today >= monday && today <= sunday;
    }, [monday, sunday]);

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 p-3 sm:p-4 space-y-3">
            {/* Week Navigation Header */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <span className="material-icons-round text-indigo-600 !text-lg">date_range</span>
                    <h4 className="text-xs sm:text-sm font-bold text-gray-800">
                        {monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – {sunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </h4>
                </div>
                <div className="flex items-center gap-1">
                    {!isCurrentWeek && (
                        <button
                            type="button"
                            onClick={jumpToToday}
                            className="px-2.5 py-1 text-[11px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors mr-1 cursor-pointer"
                        >
                            Today
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => shiftWeek(-1)}
                        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                        title="Previous week"
                    >
                        <span className="material-icons-round !text-lg">chevron_left</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => shiftWeek(1)}
                        className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                        title="Next week"
                    >
                        <span className="material-icons-round !text-lg">chevron_right</span>
                    </button>
                </div>
            </div>

            {/* 7-Day Horizontal Strip */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {weekDays.map(d => {
                    const isSelected = d.dateStr === selectedDate;
                    const isToday = d.dateStr === todayStr;
                    const hw = homeworks.find(h => h.date === d.dateStr);
                    const status = hw ? studentStatuses.find(s => s.homeworkId === hw.id && s.studentId === studentId) : undefined;
                    const totalTasks = hw?.tasks.length || 0;
                    const completedCount = status?.completedTaskIds.length || 0;
                    const isAllDone = totalTasks > 0 && completedCount === totalTasks;
                    const hasPending = totalTasks > 0 && completedCount < totalTasks;

                    return (
                        <button
                            key={d.dateStr}
                            type="button"
                            onClick={() => onSelectDate(d.dateStr)}
                            className={`flex flex-col items-center justify-between py-2 px-1 rounded-xl transition-all relative group cursor-pointer ${
                                isSelected
                                    ? 'bg-indigo-600 text-white shadow-md ring-2 ring-indigo-600/30'
                                    : isToday
                                        ? 'bg-indigo-50/80 text-indigo-950 border border-indigo-200 hover:bg-indigo-100'
                                        : 'bg-gray-50/70 text-gray-700 border border-gray-100 hover:bg-gray-100/80'
                            }`}
                        >
                            {/* Day of week (Mon, Tue...) */}
                            <span className={`text-[10px] sm:text-[11px] uppercase tracking-wider font-semibold ${
                                isSelected ? 'text-indigo-100 font-bold' : 'text-gray-400'
                            }`}>
                                {d.shortDay}
                            </span>

                            {/* Day number (1, 2, 3...) */}
                            <span className={`text-sm sm:text-base font-bold my-0.5 ${
                                isSelected ? 'text-white' : isToday ? 'text-indigo-700 font-extrabold' : 'text-gray-800'
                            }`}>
                                {d.dayNumber}
                            </span>

                            {/* Indicator: Completed Check vs Pending Dot vs Empty */}
                            <div className="h-3 flex items-center justify-center">
                                {isAllDone ? (
                                    <span 
                                        className={`material-icons-round !text-[13px] ${isSelected ? 'text-emerald-300' : 'text-emerald-600'}`}
                                        title="All tasks completed"
                                    >
                                        check_circle
                                    </span>
                                ) : hasPending ? (
                                    <span 
                                        className={`w-2 h-2 rounded-full ${isSelected ? 'bg-amber-300' : 'bg-amber-500'} ring-1 ring-white`}
                                        title={`${totalTasks - completedCount} tasks pending`}
                                    />
                                ) : (
                                    <span className="w-1.5 h-1.5 rounded-full bg-transparent" />
                                )}
                            </div>

                            {/* Today mini-pill */}
                            {isToday && !isSelected && (
                                <span className="absolute -top-1.5 px-1 py-0.2 bg-indigo-600 text-white text-[8px] font-bold rounded-full shadow-2xs">
                                    Today
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export const HomeworkHub = ({ student, user, allHomeworks, studentHomeworkStatuses, onUpdateStudentHomeworkStatus, onLogStuck, t, tutorLogs, onGetTutorHelp, onTranslateTutorInteraction, org }: {
    student: StudentConnection,
    user: UserProfile,
    allHomeworks: Homework[],
    studentHomeworkStatuses: StudentHomeworkStatus[],
    onUpdateStudentHomeworkStatus: (studentId: string, homeworkId: string, taskId: string, isComplete: boolean) => void,
    onLogStuck: (studentId: string, homeworkId: string, taskId: string) => void,
    t: (key: string) => string,
    tutorLogs: TutorLog[],
    onGetTutorHelp: (studentId: string, homeworkId: string, taskId: string, task: HomeworkTask, studentName: string, existingLog: TutorLog | null, userInput?: string, triggerType?: 'initial' | 'concept' | 'example' | 'resources' | 'question') => Promise<void>,
    onTranslateTutorInteraction: (logId: string, interactionIndex: number, text: string, targetLanguage: string) => Promise<void>,
    org: Organization | undefined
}) => {
    // View mode: 'agenda' (default, compact 7-day strip) vs 'calendar' (monthly view)
    const [viewMode, setViewMode] = useState<'agenda' | 'calendar'>('agenda');

    // Current date logic
    const todayStr = useMemo(() => formatLocalDateStr(new Date()), []);
    const [selectedDate, setSelectedDate] = useState(todayStr);
    
    // Logic for "One week past and all future" (for Calendar constraint)
    const oneWeekAgo = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d;
    }, []);
    const minDateStr = useMemo(() => formatLocalDateStr(oneWeekAgo), [oneWeekAgo]);

    // Filter homeworks based on this constraint for the CALENDAR view
    const visibleHomeworks = useMemo(() => {
        return allHomeworks.filter(h => h.date >= minDateStr);
    }, [allHomeworks, minDateStr]);

    // Current selection (searches across allHomeworks so parents can view any date)
    const selectedHomework = allHomeworks.find(h => h.date === selectedDate);
    const studentStatus = selectedHomework ? studentHomeworkStatuses.find(s => s.homeworkId === selectedHomework.id && s.studentId === student.id) : undefined;
    const completedTaskCount = studentStatus?.completedTaskIds.length || 0;
    const totalTaskCount = selectedHomework?.tasks.length || 0;
    const percentage = totalTaskCount > 0 ? (completedTaskCount / totalTaskCount) * 100 : 0;

    // Next available assignment date
    const nextAvailableHomework = useMemo(() => {
        return allHomeworks
            .filter(h => h.date > selectedDate && h.tasks.length > 0)
            .sort((a, b) => a.date.localeCompare(b.date))[0];
    }, [allHomeworks, selectedDate]);

    // -- Tutor Modal Logic --
    const [activeTutorTask, setActiveTutorTask] = useState<HomeworkTask | null>(null);
    const activeTutorLog = activeTutorTask && selectedHomework 
        ? tutorLogs.find(l => l.studentId === student.id && l.homeworkId === selectedHomework.id && l.taskId === activeTutorTask.id)
        : null;

    const handleStuck = (task: HomeworkTask) => {
        if (!selectedHomework) return;
        // 1. Log it to database (existing analytics functionality)
        onLogStuck(student.id, selectedHomework.id, task.id);
        // 2. Open the Tutor Modal to actually help
        setActiveTutorTask(task);
    };

    const handleTutorSendMessage = async (text?: string, triggerType?: any) => {
        if (!activeTutorTask || !selectedHomework) return;
        await onGetTutorHelp(
            student.id, 
            selectedHomework.id, 
            activeTutorTask.id, 
            activeTutorTask, 
            student.childName, 
            activeTutorLog || null, 
            text, 
            triggerType
        );
    };

    return (
        <div className="p-4 space-y-4">
            {/* View Switcher: Daily Agenda vs Monthly Calendar */}
            <div className="flex bg-gray-100 p-1 rounded-xl shadow-xs">
                <button
                    type="button"
                    onClick={() => setViewMode('agenda')}
                    className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                        viewMode === 'agenda'
                            ? 'bg-white text-indigo-700 shadow-xs'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    <span className="material-icons-round !text-base">view_day</span>
                    Daily Agenda & Tasks
                </button>
                <button
                    type="button"
                    onClick={() => setViewMode('calendar')}
                    className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                        viewMode === 'calendar'
                            ? 'bg-white text-indigo-700 shadow-xs'
                            : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                    <span className="material-icons-round !text-base">calendar_month</span>
                    Monthly Calendar
                </button>
            </div>

            {viewMode === 'agenda' ? (
                <div className="space-y-4 animate-fade-in">
                    {/* Rolling 7-Day Day Selector Strip */}
                    <HomeworkWeekStrip
                        selectedDate={selectedDate}
                        onSelectDate={setSelectedDate}
                        homeworks={allHomeworks}
                        studentStatuses={studentHomeworkStatuses}
                        studentId={student.id}
                        t={t}
                    />

                    {/* Homework Detail / Agenda Card */}
                    <div className="transition-all duration-300">
                        <HomeworkDigestCard 
                            homework={selectedHomework}
                            studentStatus={studentStatus}
                            onUpdateStatus={onUpdateStudentHomeworkStatus}
                            onStuck={isParentPolicyEnabled(org, 'allowParentAITutor', 'aiTutor') ? handleStuck : undefined}
                            studentId={student.id}
                            completionPercentage={percentage}
                            t={t}
                            userLanguage={user.language}
                            selectedDate={selectedDate}
                            onJumpToDate={setSelectedDate}
                            nextAvailableDate={nextAvailableHomework?.date}
                            tutorLogs={tutorLogs}
                        />
                    </div>
                </div>
            ) : (
                <div className="space-y-4 animate-fade-in">
                    {/* Monthly Calendar View */}
                    <HomeworkCalendar 
                        homeworks={visibleHomeworks}
                        selectedDate={selectedDate}
                        onSelectDate={setSelectedDate}
                        minDate={oneWeekAgo}
                    />

                    {/* Selected Day Digest */}
                    <div className="transition-all duration-300">
                        <HomeworkDigestCard 
                            homework={selectedHomework}
                            studentStatus={studentStatus}
                            onUpdateStatus={onUpdateStudentHomeworkStatus}
                            onStuck={isParentPolicyEnabled(org, 'allowParentAITutor', 'aiTutor') ? handleStuck : undefined}
                            studentId={student.id}
                            completionPercentage={percentage}
                            t={t}
                            userLanguage={user.language}
                            selectedDate={selectedDate}
                            onJumpToDate={setSelectedDate}
                            nextAvailableDate={nextAvailableHomework?.date}
                            tutorLogs={tutorLogs}
                        />
                    </div>
                </div>
            )}

            {/* Tutor Modal */}
            {activeTutorTask && selectedHomework && (
                <TutorSessionModal 
                    task={activeTutorTask}
                    studentName={student.childName}
                    orgName={org?.name || 'School'}
                    history={activeTutorLog ? activeTutorLog.interactions : []}
                    onSendMessage={handleTutorSendMessage}
                    onClose={() => setActiveTutorTask(null)}
                    userLanguage={user.language}
                />
            )}
        </div>
    );
};
